<template>
    <modal
        :show="show"
        size="lg"
        modal-classes="modal-secondary"
        @update:show="$emit('update:show', $event)"
    >
        <h6 slot="header" class="modal-title">
            {{
                isResult
                    ? $t("wms.return_move_main_result_title")
                    : $t("wms.return_move_main_title")
            }}
        </h6>

        <!-- ================= STATE 1: preview / pick rows ================= -->
        <div v-if="!isResult">
            <div class="row">
                <div class="col-md-6">
                    <base-input :label="$t('wms.return_move_main_src')">
                        <p class="form-control-static">
                            {{ info.storage_name || "-" }}
                        </p>
                    </base-input>
                    <!--
                        Mandated by the owner (Q1), and kept in the code because
                        deleting the sentence once already cost the reasoning:
                        InventoryReturnBalance takes no date argument, so this
                        modal lists the balance of the WHOLE return warehouse,
                        while the report behind the dialog is date-filtered. The
                        two sets of figures legitimately disagree and usually
                        will. Without this line the user reads that as a
                        contradiction with nothing on screen explaining it.

                        Rendered as a muted one-liner, NOT the original
                        base-alert type="warning": the owner objected to the
                        yellow box ("ไม่ต้องมีแจ้งเตือนพื้นหลังสีเหลือง"), not to
                        the sentence. Do not give it a background colour.
                    -->
                    <small class="d-block text-muted mt-1">
                        {{ noticeText }}
                    </small>
                </div>
                <div class="col-md-6">
                    <!--
                        The id is printed next to the name because the name is
                        otherwise the ONLY control an operator has over where a
                        one-way bulk move lands. The server's guards catch
                        "storage does not exist" and "source == destination";
                        nothing catches "exists, right enterprise, WRONG
                        warehouse". A bare dash is ambiguous too - it looks the
                        same whether the name is empty or the field is missing
                        entirely - and the id tells those apart.
                    -->
                    <base-input :label="$t('wms.return_move_main_dest')">
                        <p class="form-control-static">
                            {{ info.dest_storage_name || "-" }}
                            <small
                                v-if="info.dest_storage_id"
                                class="text-muted"
                            >
                                (#{{ info.dest_storage_id }})
                            </small>
                        </p>
                    </base-input>
                </div>
            </div>

            <div v-if="isLoading" class="text-center py-4">
                <i class="fas fa-spinner fa-spin"></i>
            </div>

            <div v-else-if="rows.length === 0" class="text-center py-4">
                <p class="mb-0 text-muted">
                    {{ $t("wms.return_move_main_empty") }}
                </p>
            </div>

            <div v-else>
                <div class="row">
                    <div class="col-md-6">
                        <base-input
                            v-model="searchQuery"
                            prepend-icon="fas fa-search"
                            :placeholder="$t('search')"
                        >
                        </base-input>
                    </div>
                    <div class="col-md-6 text-md-right">
                        <base-button
                            :type="selectAllMode ? 'secondary' : 'primary'"
                            size="sm"
                            @click="toggleSelectAll()"
                        >
                            {{
                                selectAllMode
                                    ? $t("wms.return_move_main_clear_all")
                                    : $t("wms.return_move_main_select_all")
                            }}
                        </base-button>
                        <!--
                            Say why the tick column disappeared, and name the way
                            back, so the state does not look like a glitch.
                        -->
                        <small
                            v-if="selectAllMode"
                            class="d-block text-muted mt-1"
                        >
                            {{ $t("wms.return_move_main_all_selected_hint") }}
                        </small>
                    </div>
                </div>

                <el-table
                    :data="queriedData"
                    row-key="product_id"
                    header-row-class-name="thead-light"
                    max-height="340"
                >
                    <!--
                        el-checkbox, not base-checkbox. base-checkbox renders
                        Bootstrap's .custom-control markup, whose 16px indicator
                        is a ::before pinned at left:-1.75rem and only made
                        visible by the 1.75rem padding-left on .custom-control.
                        Two element-ui rules destroy that inside a table cell:
                          * ".el-table .cell,.el-table th div{overflow:hidden;
                            text-overflow:ellipsis;padding-left:10px}" - the
                            "th div" half matches the .custom-control div itself
                            and resets its padding to 10px, so the indicator
                            lands 2px LEFT of the cell and is clipped away. That
                            is the blank select-all in the header.
                          * the cell is only 23px wide (55px column minus the
                            2x16px td padding this project adds in
                            core/vendors/_element-tables.scss), so the 28px-wide
                            .custom-control overflows and the cell paints an
                            ellipsis over it. That is the "..." in the rows.
                        el-checkbox draws a normal 16px in-flow box that needs no
                        negative offset, so it survives both rules. Measured in
                        headless Chrome against the compiled argon.css.
                    -->
                    <!--
                        label-class-name puts a class on this column's <th>,
                        which is the only hook that centres the select-all over
                        the row checkboxes below it. align="center" alone does
                        not: argon makes every header .cell a flex container
                        (core/vendors/_element-tables.scss) and flex ignores
                        text-align, leaving the select-all 3.5px to the left of
                        the column. Do NOT "fix" that with a wrapper <div>
                        instead -- `.el-table th div` clips the control; see the
                        comment on the header template below.
                    -->
                    <!--
                        width="55" is load-bearing, not a taste: 55 - 2*16px of
                        td padding (_element-tables.scss) leaves a 23px content
                        box for a 16px control, i.e. 7px of headroom. A verifier
                        measured width="40" halving the checkbox to 8 visible
                        and 8 clickable pixels, with every test still green --
                        jsdom has no geometry, so nothing here can catch it.
                        For the same reason never add `show-overflow-tooltip` to
                        this column: it applies `.cell.el-tooltip{min-width:50px}`,
                        which overflows the 23px box by 31px and pushes the
                        checkbox onto the next column.
                    -->
                    <el-table-column
                        v-if="!selectAllMode"
                        width="55"
                        align="center"
                        label-class-name="header-cell-center"
                    >
                        <!--
                            element-ui 2.4.11 only honours a header slot via
                            $scopedSlots, so slot-scope must be present even
                            though it is unused. Without it the header renders
                            blank and there is no select-all.
                        -->
                        <template slot="header" slot-scope="scope">
                            <el-checkbox
                                v-model="allSelected"
                                :indeterminate="someSelected"
                            >
                            </el-checkbox>
                        </template>
                        <template slot-scope="{ row }">
                            <el-checkbox v-model="row._checked"></el-checkbox>
                        </template>
                    </el-table-column>

                    <el-table-column
                        :label="$t('wms.product')"
                        min-width="220px"
                    >
                        <template slot-scope="{ row }">
                            <span class="d-block">{{ row.product_name }}</span>
                        </template>
                    </el-table-column>

                    <el-table-column
                        prop="balance"
                        :label="$t('wms.return_available')"
                        min-width="120px"
                        align="right"
                    >
                    </el-table-column>
                </el-table>

                <div
                    class="d-flex justify-content-between align-items-center flex-wrap mt-2"
                >
                    <p class="card-category mb-0">
                        {{ $t("wms.return_move_main_selected") }}
                        {{ selectedRows.length }} / {{ rows.length }}
                        &nbsp;&middot;&nbsp;
                        {{ $t("wms.return_move_main_total") }}
                        {{ selectedQuantity }}
                    </p>
                    <base-pagination
                        class="pagination-no-border mb-0"
                        v-model="pagination.currentPage"
                        :per-page="pagination.perPage"
                        :total="total"
                    >
                    </base-pagination>
                </div>

                <base-input
                    v-model="note"
                    :label="$t('wms.note')"
                    input-classes="form-control-alternative"
                >
                </base-input>
            </div>
        </div>

        <!-- ================= STATE 2: result of the move ================= -->
        <div v-else>
            <div class="row">
                <div class="col-md-6">
                    <base-input :label="$t('wms.return_move_main_src')">
                        <p class="form-control-static">
                            {{ info.storage_name || "-" }}
                        </p>
                    </base-input>
                </div>
                <div class="col-md-6">
                    <base-input :label="$t('wms.return_move_main_dest')">
                        <p class="form-control-static">
                            {{ info.dest_storage_name || "-" }}
                            <small
                                v-if="info.dest_storage_id"
                                class="text-muted"
                            >
                                (#{{ info.dest_storage_id }})
                            </small>
                        </p>
                    </base-input>
                </div>
            </div>

            <!--
                The figures below come from the POST response, never from the
                preview: the balance can change between opening this modal and
                confirming, and echoing the preview numbers would tell the user
                something untrue.
            -->
            <base-alert type="info" class="py-2">
                <small>{{ $t("wms.return_move_main_result_note") }}</small>
            </base-alert>

            <!--
                moved_count 0 with a populated skipped list is a valid 200, not
                a failure. The toast is dismissable, so state it here too.
            -->
            <base-alert
                v-if="resultMovedCount === 0"
                type="warning"
                class="py-2"
            >
                <small>{{ $t("wms.return_move_main_none") }}</small>
            </base-alert>

            <el-table
                :data="resultRows"
                row-key="product_id"
                header-row-class-name="thead-light"
                max-height="340"
            >
                <el-table-column :label="$t('wms.product')" min-width="220px">
                    <template slot-scope="{ row }">
                        {{ row.product_name }}
                    </template>
                </el-table-column>

                <el-table-column
                    :label="$t('wms.return_move_main_moved')"
                    min-width="120px"
                    align="right"
                >
                    <template slot-scope="{ row }">
                        {{ row.quantity }}
                    </template>
                </el-table-column>

                <el-table-column
                    :label="$t('status')"
                    min-width="180px"
                >
                    <template slot-scope="{ row }">
                        <badge v-if="row.moved" type="success">
                            {{ $t("wms.return_move_main_moved_ok") }}
                        </badge>
                        <badge v-else type="warning">
                            {{ $t("wms.return_move_main_skipped") }}
                        </badge>
                    </template>
                </el-table-column>
            </el-table>

            <p class="card-category mt-2 mb-0">
                {{ $t("wms.return_move_main_moved_ok") }}
                {{ resultMovedCount }} / {{ resultRows.length }}
                &nbsp;&middot;&nbsp;
                {{ $t("wms.return_move_main_total") }}
                {{ resultMovedQuantity }}
                <span v-if="result.head_id">
                    &nbsp;&middot;&nbsp; {{ $t("wms.bill_number") }}
                    {{ result.head_id }}
                </span>
            </p>
        </div>

        <template slot="footer">
            <base-button
                v-if="!isResult"
                type="success"
                :loading="isSubmitting"
                :disabled="selectedRows.length === 0 || !destinationKnown"
                @click="handleConfirm()"
            >
                {{ $t("wms.return_move_main_confirm") }}
            </base-button>
            <base-button
                v-if="!isResult"
                type="link"
                class="ml-auto"
                @click="close()"
            >
                {{ $t("cancel") }}
            </base-button>

            <base-button v-else type="primary" class="ml-auto" @click="close()">
                {{ $t("close") }}
            </base-button>
        </template>
    </modal>
</template>
<script>
import { Table, TableColumn, Checkbox } from "element-ui";
import clientPaginationMixin from "../PaginatedTables/clientPaginationMixin";
import swal from "sweetalert2";
import { mapActions } from "vuex";

export default {
    name: "return-move-to-main-modal",
    mixins: [clientPaginationMixin],
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Checkbox.name]: Checkbox
    },
    props: {
        show: Boolean
    },
    data() {
        return {
            // barcode is deliberately still searchable even though the cell
            // no longer prints it: staff identify a SKU by scanning, and the
            // scanner types the code into this box. Dropping it here would
            // turn a row-height change into a silent search regression.
            // It stays in the row objects built by fetchBalance() for the
            // same reason - a key here with no matching field breaks search.
            propsToSearch: ["product_name", "barcode"],
            isLoading: false,
            isSubmitting: false,
            stage: "preview",
            info: {},
            rows: [],
            note: "",
            result: {},
            resultRows: [],
            // Explicit "the user pressed the select-all button" flag. It is NOT
            // the same as "every row happens to be ticked": someone who ticks
            // the last row by hand must not have the column vanish under their
            // cursor, so only this flag hides it.
            selectAllMode: false,
            // product ids sent in the last POST, so a row the server does not
            // mention at all can still be reported instead of vanishing.
            requestedIds: []
        };
    },
    computed: {
        // consumed by clientPaginationMixin
        tableData() {
            return this.rows;
        },
        isResult() {
            return this.stage === "result";
        },
        // The move is one-way, irreversible in bulk, and lands wherever
        // app.storage_for_return_good_destination points - which this screen
        // learns ONLY from the response. If the response carries no destination
        // name the label renders a bare "-", and a live confirm button under a
        // dash is an invitation to empty the return warehouse into a warehouse
        // nobody on screen can name.
        //
        // That is not hypothetical: it is exactly what a version-skewed
        // deployment looks like. Roll the API first and the old bundle asks for
        // a field the new API stopped sending; roll the UI first and the new
        // bundle asks for a field the old API does not send yet. Either way the
        // label degrades to "-" while the button stays green, and the modal
        // heading still names the warehouse the stock used to go to.
        //
        // Deploy order is a human procedure that nothing enforces, so the guard
        // lives here instead. It fails closed in BOTH skew directions: no name,
        // no move. A destination that genuinely has an empty name also blocks -
        // correct, because then the operator cannot verify the target either.
        destinationKnown() {
            return !!this.info.dest_storage_name;
        },
        // Name the source warehouse from the response instead of hardcoding it.
        noticeText() {
            return this.$t("wms.return_move_main_notice", {
                storage:
                    this.info.storage_name ||
                    this.$t("wms.menu.return_warehouse")
            });
        },
        selectedRows() {
            return this.rows.filter(r => r._checked);
        },
        selectedQuantity() {
            return this.selectedRows.reduce(
                (sum, r) => sum + (Number(r.balance) || 0),
                0
            );
        },
        // Drives the header checkbox's dash state: some but not all ticked.
        someSelected() {
            return (
                this.selectedRows.length > 0 &&
                this.selectedRows.length < this.rows.length
            );
        },
        allSelected: {
            get() {
                return (
                    this.rows.length > 0 &&
                    this.selectedRows.length === this.rows.length
                );
            },
            set(value) {
                this.rows.forEach(r => {
                    r._checked = value;
                });
            }
        },
        resultMovedQuantity() {
            if (this.result && this.result.moved_quantity != null) {
                return Number(this.result.moved_quantity) || 0;
            }
            return this.resultRows.reduce(
                (sum, r) => sum + (r.moved ? Number(r.quantity) || 0 : 0),
                0
            );
        },
        resultMovedCount() {
            if (this.result && this.result.moved_count != null) {
                return Number(this.result.moved_count) || 0;
            }
            return this.resultRows.filter(r => r.moved).length;
        }
    },
    methods: {
        ...mapActions({
            InventoryReturnBalance: "WmsInventory/InventoryReturnBalance",
            InventoryMoveReturnToMain: "WmsInventory/InventoryMoveReturnToMain"
        }),
        close() {
            this.$emit("update:show", false);
        },
        // Ticks / unticks EVERY row, deliberately driving this.rows rather than
        // queriedData. queriedData is one page of the current search result, but
        // handleConfirm() moves every ticked row in this.rows, so a "select all"
        // that stopped at the visible page would misstate what is about to move.
        toggleSelectAll() {
            let next = !this.selectAllMode;
            this.rows.forEach(r => {
                r._checked = next;
            });
            this.selectAllMode = next;
        },
        reset() {
            this.stage = "preview";
            this.info = {};
            this.rows = [];
            this.note = "";
            this.result = {};
            this.resultRows = [];
            this.requestedIds = [];
            this.selectAllMode = false;
            this.searchQuery = "";
            this.pagination.currentPage = 1;
        },
        showError(err) {
            // Msg is the stable machine code and doubles as the i18n key.
            // Guard the whole chain: a network failure has no err.response.
            let data = (err && err.response && err.response.data) || {};
            let code = data.Msg || "errors.internal";
            let title = this.$t(code);
            // A key that resolves to an object (nested block) would render as
            // [object Object]; fall back to the raw code instead.
            if (typeof title !== "string") {
                title = code;
            }
            // Optional "<code>_detail" companion key. swal renders `title` bold
            // and large, so the title has to be the sentence the person in front
            // of the screen can act on ("this screen is not configured yet, call
            // IT") while the part only IT can act on (the config key name, the
            // restart) belongs in the smaller body text. Keys without a
            // companion are unaffected.
            //
            // vue-i18n echoes the key path back when a key is missing, and the
            // unit tests mock $t as the identity function, so "the translation
            // equals the key I asked for" is the reliable missing-key test.
            let detailKey = code + "_detail";
            let detail = this.$t(detailKey);
            if (typeof detail !== "string" || detail === detailKey) {
                detail = "";
            }
            // A companion wins over the server's Cause. We only author one for
            // errors where we know the operator's next step better than the
            // server does; letting a raw Cause suppress "call IT, and here is
            // the key IT needs" would undo the whole point of the split.
            //
            // Cause is type-guarded for the same reason `title` is. Today the
            // Go side always formats it through ErrorCustom(), so it is a
            // string in practice - but it arrives over the wire from a JSON
            // body this component does not control, and swal stringifies
            // whatever it is handed. An object here would print the literal
            // "[object Object]" at the operator, which is the failure this
            // repository has already shipped once (a duplicate i18n key made
            // $t('platform') resolve to a nested block). Defence in depth.
            swal.fire({
                title: title,
                text:
                    detail ||
                    (typeof data.Cause === "string" ? data.Cause : "") ||
                    "",
                icon: "error",
                confirmButtonClass: "btn btn-danger btn-fill",
                buttonsStyling: false
            });
            return code;
        },
        fetchBalance() {
            this.isLoading = true;
            return this.InventoryReturnBalance()
                .then(resp => {
                    this.isLoading = false;
                    let datum = (resp && resp.data && resp.data.Datum) || {};
                    this.info = datum;
                    let list = datum.list || [];
                    // Zero balances are excluded server-side; filter defensively
                    // so a stray row can never offer a no-op move.
                    this.rows = list
                        .filter(it => Number(it.balance) > 0)
                        .map(it => ({
                            product_id: Number(it.product_id),
                            product_name: it.product_name || "#" + it.product_id,
                            barcode: it.barcode || "",
                            inventory_id: Number(it.inventory_id) || 0,
                            balance: Number(it.balance) || 0,
                            // Rows start UNTICKED, by the owner's decision (#224).
                            // Every ticked SKU moves its WHOLE balance and there is no
                            // quantity to edit, so a default of all-ticked makes one
                            // careless confirm the largest possible action — it empties
                            // the return warehouse. The stock is not lost (it lands in
                            // the configured destination warehouse and the head row
                            // records the direction, so it can be traced and moved
                            // back), but undoing it is manual and per-SKU. Making the
                            // operator choose costs one click and makes the intent
                            // explicit.
                            _checked: false
                        }));
                    this.pagination.currentPage = 1;
                })
                .catch(err => {
                    this.isLoading = false;
                    this.rows = [];
                    this.showError(err);
                });
        },
        handleConfirm() {
            // Mirrors the button's :disabled. The disabled attribute is a UI
            // affordance, not an access control - stubbed buttons in tests, a
            // stray Enter key and any future caller all reach this method
            // directly - so the destination guard is repeated where the request
            // is actually issued.
            if (
                this.selectedRows.length === 0 ||
                this.isSubmitting ||
                !this.destinationKnown
            ) {
                return;
            }
            this.isSubmitting = true;

            // product_id only, by contract:
            //  - no quantity: the user picks rows, never amounts, and each
            //    picked SKU moves whatever the server finds under its row lock;
            //  - no inventory_id: the move helper trusts a raw inventory id
            //    without checking which storage it belongs to, so the route
            //    deliberately refuses to accept one from the client.
            // Dedupe defensively so we can never trip the server's
            // duplicate_product refusal with an error the user cannot act on.
            let seen = {};
            let ids = [];
            this.selectedRows.forEach(r => {
                let pid = Number(r.product_id);
                if (!pid || seen[pid]) {
                    return;
                }
                seen[pid] = true;
                ids.push(pid);
            });
            this.requestedIds = ids;
            let payload = {
                list: ids.map(pid => ({ product_id: pid })),
                note: this.note
            };

            this.InventoryMoveReturnToMain(payload)
                .then(resp => {
                    this.isSubmitting = false;
                    let datum = (resp && resp.data && resp.data.Datum) || {};
                    this.result = datum;
                    this.resultRows = this.buildResultRows(datum);
                    this.stage = "result";
                    // "Everything was already gone" is a legitimate 200 with
                    // moved_count 0, head_id 0 and a populated skipped list -
                    // a success toast there would be a lie, but it is not an
                    // error either. Say plainly that nothing moved.
                    let movedNothing = this.resultMovedCount === 0;
                    swal.fire({
                        title: movedNothing
                            ? this.$t("wms.return_move_main_none")
                            : this.$t("wms.return_move_main_success"),
                        text: "",
                        icon: movedNothing ? "warning" : "success",
                        confirmButtonClass: movedNothing
                            ? "btn btn-warning btn-fill"
                            : "btn btn-success btn-fill",
                        buttonsStyling: false
                    });
                })
                .catch(err => {
                    this.isSubmitting = false;
                    this.showError(err);
                });
        },
        // Render exactly what the server reports. Rows it skipped because the
        // balance had already reached zero must be shown, not dropped.
        buildResultRows(datum) {
            let rows = [];
            let seen = {};
            let nameOf = pid => {
                let match = this.rows.find(
                    r => Number(r.product_id) === Number(pid)
                );
                return (match && match.product_name) || "#" + pid;
            };
            let push = (pid, qty, moved, extra) => {
                if (seen[pid]) {
                    return;
                }
                seen[pid] = true;
                rows.push({
                    product_id: pid,
                    product_name: (extra && extra.product_name) || nameOf(pid),
                    quantity: qty,
                    moved: moved,
                    return_balance_after: extra
                        ? extra.return_balance_after
                        : undefined
                });
            };

            // datum.list = what was actually written under the row locks.
            let moved = datum.list || [];
            moved.forEach(it => {
                let qty = Number(it.quantity);
                qty = isNaN(qty) ? 0 : qty;
                // A zero-quantity line is a skipped line however it is spelled.
                push(Number(it.product_id), qty, qty > 0, it);
            });

            // datum.skipped = picked, but the balance had already reached zero.
            let skipped = datum.skipped || [];
            skipped.forEach(it => {
                let isObj = it !== null && typeof it === "object";
                let pid = Number(isObj ? it.product_id : it);
                push(pid, 0, false, isObj ? it : null);
            });

            // Anything picked that the response never mentioned is still
            // unaccounted for — surface it rather than letting it disappear.
            this.requestedIds.forEach(pid => {
                push(Number(pid), 0, false, null);
            });

            return rows;
        }
    },
    watch: {
        show(value) {
            if (value) {
                this.reset();
                this.fetchBalance();
            }
        }
    },
    mounted() {
        this.pagination.perPage = 10;
    }
};
</script>
